import { isEqual } from 'lodash'
import type * as Flows from './types.no-deps'
import { currentAnswerIsEmpty, findAnswer } from './answer.no-deps'
import { evaluateCalculation } from './evaluateCalculation.no-deps'
import { assumeExhaustion } from '../../lib/typeHelpers.no-deps'
import { answerHasChoices } from './typeGuards.no-deps'
import type { Dictionary } from '../../lib/functional/dictionary.no-deps'
import type { UnitData } from '../units/types.no-deps'
import { findCalculationResult } from './summary.no-deps'
import type { LoopPathIds } from './LoopPath.no-deps'

type Element = Flows.Element | Flows.NestedElement | Flows.Section

export function evaluateCondition(
    summary: Pick<Flows.SectionSummary, 'answers' | 'calculations'>,
    condition: Flows.RouteCondition,
    elementId: Flows.ElementId,
    loopPathIds: LoopPathIds,
    elementsLookup: Dictionary<Element>,
    subjectUnitData: UnitData,
    elementToStepLookup: Map<Flows.ElementId, Flows.Step>
): boolean {
    const answer = findAnswer(summary.answers, elementId, loopPathIds)
    const calculation = findCalculationResult(summary, elementId, loopPathIds)

    switch (condition.type) {
        case 'NotAnsweredRouteCondition':
            return currentAnswerIsEmpty(answer)

        case 'YesNoRouteCondition':
            if (answer == null) return false
            return answer.value === condition.equalTo

        case 'ConfirmedRouteCondition':
            if (answer == null) return false
            return answer.value === true

        case 'NumberRouteCondition':
            if (answer == null && calculation == null) return false

            const elementValue = answer != null ? answer.value : calculation.value
            const expressionValue = evaluateCalculation(
                condition.expression,
                summary.answers,
                summary.calculations,
                elementsLookup,
                subjectUnitData,
                loopPathIds,
                elementToStepLookup
            )

            if (elementValue == null) return false
            if (expressionValue == null) return false

            switch (condition.operator) {
                case '<=':
                    return +elementValue <= expressionValue
                case '<':
                    return +elementValue < expressionValue
                case '==':
                    return elementValue === expressionValue
                case '!=':
                    return elementValue !== expressionValue
                case '>':
                    return +elementValue > expressionValue
                case '>=':
                    return +elementValue >= expressionValue
                default:
                    assumeExhaustion(condition.operator)
                    return false
            }

        case 'ContainsChoiceRouteCondition':
            if (answer == null || !answerHasChoices(answer)) return false
            return answer.value.includes(condition.choiceId)

        case 'EqualsChoicesRouteCondition':
            if (answer == null || !answerHasChoices(answer)) return false
            return isEqual(answer.value, condition.equalTo)

        case 'EqualsChoiceRouteCondition':
            if (answer == null || answer.type !== 'radio') return false
            return answer.value === condition.equalTo

        case 'ContainsStringRouteCondition':
            if (answer == null || answer.type !== 'text-capture') return false
            return answer.value.includes(condition.string)

        case 'SectionRouteCondition':
            return condition.completed ? answer != null : answer == null

        default:
            assumeExhaustion(condition)
            return false
    }
}

export function evaluateUnitDataCondition(condition: Flows.UnitDataConditionalRoute, subjectUnitData: UnitData) {
    const lookup = subjectUnitData[condition.variableName]
    switch (condition.operator) {
        case '<=':
            return Number(lookup) <= Number(condition.value)
        case '<':
            return Number(lookup) < Number(condition.value)
        case '==':
            // We explictly want to convert types as the values could be strings
            // or numbers
            /* eslint-disable-next-line eqeqeq */
            return lookup == condition.value
        case '!=':
            /* eslint-disable-next-line eqeqeq */
            return lookup != condition.value
        case '>':
            return Number(lookup) > Number(condition.value)
        case '>=':
            return Number(lookup) >= Number(condition.value)
        default:
            assumeExhaustion(condition.operator)
            return false
    }
}
